import { supabase } from './supabase'

// One read of the brand's campaign list, shared by every screen that needs it.
//
// Six screens each fetched their own copy — Applications, Invited Creators,
// Creator Roster, Messages, ROI & Analytics, Sample Requests — so moving
// between them refetched the same handful of rows every time. They asked for
// overlapping-but-different column sets and slightly different filters, which
// is why it never looked like duplication.
//
// This reads the superset once and lets each caller filter it. The column set
// is the union of what the six asked for; they are all small scalars, so
// over-fetching a few of them costs far less than a second round trip.
//
// NOT a general data cache. The TTL exists to collapse a burst of navigation,
// nothing more, and every write path that changes the list calls
// invalidateBrandCampaigns() so a newly created or edited campaign shows up at
// once rather than up to a minute later.

export interface BrandCampaign {
  id: string
  name: string
  product_name: string | null
  status: string | null
  usage_rights_mode: string | null
  usage_rights_default_pct: number | null
  budget_cap_cents: number | null
  budget_spent_cents: number | null
  budget_pending_cents: number | null
  allowed_package_sizes: number[] | null
  is_viral_remake: boolean | null
  /* Multi-product campaigns (2026-09-14). product_name above is only ever
   * product #1 on one of these, so every screen fed by this read was naming
   * one product and hiding the rest — see campaignProductIdentity in
   * lib/campaignProduct.ts, which these two columns exist to feed. Two more
   * small scalars on a read that is already the union of six screens' needs. */
  is_multi_product: boolean | null
  bundle_product_name: string | null
  /* Creator Bonus (2026-09-29, optional). Both null = no bonus on this campaign. */
  bonus_amount_cents: number | null
  bonus_gmv_threshold_cents: number | null
  /* Optional, brand-entered (2026-09-25) — see TiktokCollabTag. */
  tiktok_collab_type: string | null
}

const COLUMNS =
  'id, name, product_name, status, usage_rights_mode, usage_rights_default_pct, ' +
  'budget_cap_cents, budget_spent_cents, budget_pending_cents, allowed_package_sizes, is_viral_remake, ' +
  'is_multi_product, tiktok_collab_type, bundle_product_name, bonus_amount_cents, bonus_gmv_threshold_cents'

/** Long enough to span moving between screens; short enough that staleness is
 *  not something anyone has to reason about. Writes invalidate explicitly. */
const TTL_MS = 60_000

interface Entry {
  brandId: string
  at: number
  promise: Promise<BrandCampaign[]>
}

let entry: Entry | null = null

async function fetchCampaigns(brandId: string): Promise<BrandCampaign[]> {
  const { data, error } = await supabase
    .from('cr_campaigns')
    .select(COLUMNS)
    .eq('brand_id', brandId)
    .order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []) as unknown as BrandCampaign[]
}

/**
 * Every campaign for the brand, newest first — ALL statuses.
 *
 * Callers that only want live campaigns filter with `activeOnly` below; that
 * used to be a `.eq('status', 'active')` on four of the six queries, and doing
 * it client-side is what lets one read serve all of them.
 */
export function readBrandCampaigns(
  brandId: string,
  opts: { force?: boolean } = {},
): Promise<BrandCampaign[]> {
  const now = Date.now()
  if (!opts.force && entry && entry.brandId === brandId && now - entry.at < TTL_MS) {
    return entry.promise
  }
  const promise = fetchCampaigns(brandId)
  entry = { brandId, at: now, promise }
  // A failed read must not be served to the next caller for the rest of the TTL.
  void promise.catch(() => {
    if (entry?.promise === promise) entry = null
  })
  return promise
}

/** Call after creating, editing, archiving or duplicating a campaign. */
export function invalidateBrandCampaigns(): void {
  entry = null
}

/** The live campaigns — what the hiring surfaces mean by "a campaign". */
export function activeOnly(rows: BrandCampaign[]): BrandCampaign[] {
  return rows.filter(c => c.status === 'active')
}
